/**
 * One "Add place" form for Plan and the live "Add to shoot" sheet. Places are:
 *  Airport      search ICAO / name, or tap a nearby one → a mission airport, "We're at" or "Flies from / to"
 *  Camera spot  where a camera stands (a Static camera can be added right here)
 *  Team spot    staging / where a mobile team waits
 *  Other        point of interest, basecamp, parking, landing zone, hazard
 * Where = Here (my location) · On the map · (no lat/lon typing).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { distanceM, type Airport, type LatLon, type LocationKind, type MissionBundle, type MissionLocation } from '@apos/shared';
import { Button, Field, IconButton, Segmented, Select, Spinner, TextArea, TextInput } from '../../components/ui';
import { api } from '../../lib/api';
import { fmt } from '../../lib/fmt';
import { LOCATION_KIND_LABEL } from '../../app/labels';
import type { Mutate } from './mutate';
import {
  AIRPORT_ROLE_LABEL, AIRPORT_USE_OPTIONS, defaultPlaceName, kindForPlaceType, OTHER_PLACE_KINDS, PLACE_TYPE_OPTIONS, placeTypeOf, withAirport, withoutAirport,
  type AirportUse, type PlaceType,
} from './setupModel';
import { useMapPick, WherePicker } from './WherePicker';
import './setup.css';
import { InlineError } from './FormBits';

export interface PlaceFormProps {
  bundle: MissionBundle;
  value: MissionLocation | 'new';
  initialType?: PlaceType;
  mutate: Mutate;
  /** After save / remove (null), or "done" on the airport list. */
  onDone(saved: MissionLocation | null): void;
  onCancel(): void;
  /** "Add a static camera here" (camera spots). */
  onCameraHere?(at: LatLon, locationId: string): void;
  render(body: ReactNode, footer: ReactNode): ReactNode;
}

export function PlaceForm({ bundle, value, initialType = 'airport', mutate, onDone, onCancel, onCameraHere, render }: PlaceFormProps) {
  const isNew = value === 'new';
  const [type, setType] = useState<PlaceType>(value === 'new' ? initialType : placeTypeOf(value.kind));
  const [otherKind, setOtherKind] = useState<LocationKind>(value !== 'new' && placeTypeOf(value.kind) === 'other' ? value.kind : 'poi');
  const kind = type === 'airport' ? null : kindForPlaceType(type, otherKind);
  const names = bundle.locations.filter((l) => value === 'new' || l.id !== value.id).map((l) => l.name);
  const [name, setName] = useState(value === 'new' ? '' : value.name);
  const [at, setAt] = useState<LatLon | null>(value === 'new' ? null : { lat: value.lat, lon: value.lon });
  const [notes, setNotes] = useState(value === 'new' ? '' : value.notes);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pick = useMapPick(bundle);
  const placeholder = kind ? defaultPlaceName(kind, names) : '';

  const save = async (thenCamera = false) => {
    if (!kind) return;
    if (!at) return setErr('Set where it is: “Here (my location)” or “On the map”.');
    setErr(null);
    setBusy(true);
    const body = { name: (name.trim() || placeholder).toUpperCase(), kind, lat: at.lat, lon: at.lon };
    const r = await mutate(isNew ? 'add the place' : 'save the place', () =>
      value === 'new' ? api.locations.create(bundle.mission.id, { ...body, elevM: null, radiusM: null, notes }) : api.locations.update(bundle.mission.id, value.id, { ...body, notes }),
    );
    setBusy(false);
    if (!r) return;
    onDone(r);
    if (thenCamera) onCameraHere?.({ lat: r.lat, lon: r.lon }, r.id);
  };
  const remove = async () => {
    if (value === 'new') return;
    const r = await mutate('remove the place', () => api.locations.remove(bundle.mission.id, value.id).then(() => true));
    if (r) onDone(null);
  };

  const body = (
    <div className="setup-form stack-16">
      {isNew ? <Segmented<PlaceType> label="Kind of place" block size="lg" className="setup-seg4" value={type} onChange={(t) => (setType(t), setErr(null))} options={PLACE_TYPE_OPTIONS} /> : null}
      {type === 'airport' ? (
        <AirportPicker bundle={bundle} mutate={mutate} />
      ) : (
        <>
          {type === 'other' ? (
            <Field label="What is it">
              {(id) => (
                <Select id={id} value={otherKind} onChange={(e) => setOtherKind(e.target.value as LocationKind)}>
                  {OTHER_PLACE_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {LOCATION_KIND_LABEL[k]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : !isNew ? (
            <Segmented<PlaceType> label="Kind of place" block value={type} onChange={setType} options={PLACE_TYPE_OPTIONS.filter((o) => o.value !== 'airport')} />
          ) : null}
          <WherePicker
            bundle={bundle}
            places={false}
            value={at}
            onChange={(p) => { setErr(null); setAt(p); }}
            onPickOnMap={() => pick.start(`Tap the map where the ${type === 'camera' ? 'camera spot' : type === 'team' ? 'team spot' : 'place'} is`, (p) => { setErr(null); setAt(p); })}
          />
          {err ? <InlineError>{err}</InlineError> : null}
          <Field label="Name" optional hint={isNew ? `Defaults to ${placeholder}` : undefined}>
            {(id) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} placeholder={placeholder} maxLength={80} />}
          </Field>
          {!isNew ? (
            <Field label="Notes" optional>
              {(id) => <TextArea id={id} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />}
            </Field>
          ) : null}
        </>
      )}
    </div>
  );

  const footer =
    type === 'airport' ? (
      <div className="setup-foot">
        <span className="setup-foot__spacer" />
        <Button variant="primary" onClick={() => onDone(null)}>
          Done
        </Button>
      </div>
    ) : (
      <div className="setup-foot">
        {!isNew ? (
          <Button variant="danger" icon="trash" onClick={() => void remove()}>
            Remove
          </Button>
        ) : null}
        <span className="setup-foot__spacer" />
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        {type === 'camera' && onCameraHere ? (
          <Button icon="camera" onClick={() => void save(true)} disabled={busy}>
            {isNew ? 'Add + camera here' : 'Camera here'}
          </Button>
        ) : null}
        <Button variant="primary" onClick={() => void save()} loading={busy}>
          {isNew ? 'Add place' : 'Save place'}
        </Button>
      </div>
    );

  if (pick.overlay) return <>{pick.overlay}</>;
  return <>{render(body, footer)}</>;
}

/** Mission airports: what is on the shoot (with its use) + search / nearby → one tap adds. */
function AirportPicker({ bundle, mutate }: { bundle: MissionBundle; mutate: Mutate }) {
  const current = bundle.missionAirports;
  const [use, setUse] = useState<AirportUse>(() => (current.some((a) => a.role === 'primary') ? 'fromto' : 'at'));
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Airport[]>([]);
  const [near, setNear] = useState<Airport[]>([]);
  const [searching, setSearching] = useState(false);
  const c = bundle.mission.center;
  useEffect(() => {
    api.airports
      .near(c.lat, c.lon, 40)
      .then(setNear)
      .catch(() => setNear([]));
  }, [c.lat, c.lon]);
  useEffect(() => {
    if (q.trim().length < 2) return setResults([]);
    const ac = new AbortController();
    setSearching(true);
    const t = setTimeout(() => {
      api.airports
        .search(q.trim(), ac.signal)
        .then(setResults)
        .catch(() => undefined)
        .finally(() => setSearching(false));
    }, 200);
    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [q]);

  const save = (list: Array<{ icao: string; role: (typeof current)[number]['role'] }>) => mutate('update the airports', () => api.missionAirports.set(bundle.mission.id, list));
  const add = (icao: string) => {
    setQ('');
    void save(withAirport(current, icao, use));
  };
  const nameOf = useMemo(() => new Map([...bundle.airports, ...near, ...results].map((a) => [a.icao, a] as const)), [bundle.airports, near, results]);
  const suggestions = near.filter((a) => !current.some((x) => x.icao === a.icao)).slice(0, 6);

  return (
    <div className="stack-16" data-testid="airport-picker">
      {current.length ? (
        <ul className="setup-list" aria-label="Airports on this shoot">
          {current.map((a) => {
            const ap = nameOf.get(a.icao);
            return (
              <li key={a.icao} className="setup-list__item">
                <b className="num">{a.icao}</b>
                <span className="setup-list__sub">
                  {AIRPORT_ROLE_LABEL[a.role]}
                  {ap ? ` · ${ap.name}` : ''}
                </span>
                <IconButton icon="trash" size="sm" label={`Remove ${a.icao}`} onClick={() => void save(withoutAirport(current, a.icao))} />
              </li>
            );
          })}
        </ul>
      ) : null}
      <Segmented<AirportUse> label="This airport is where" block value={use} onChange={setUse} options={AIRPORT_USE_OPTIONS} />
      <p className="setup-hint">{use === 'at' ? 'Where the crew is filming (weather comes from here).' : 'Where the Subject or Chase departs or lands.'}</p>
      <div className="plan-search">
        <TextInput icon="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ICAO, name or city" aria-label="Search airports" autoComplete="off" />
        {searching ? <Spinner size={14} /> : null}
      </div>
      {results.length ? (
        <ul className="plan-results" role="listbox" aria-label="Airports found">
          {results.slice(0, 8).map((a) => (
            <li key={a.icao}>
              <button type="button" role="option" aria-selected={false} onClick={() => add(a.icao)}>
                <b className="num">{a.icao}</b>
                <span>{a.name}</span>
                {a.city ? <span className="muted">{a.city}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {suggestions.length && !q ? (
        <div className="plan-suggest" role="group" aria-label="Nearby airports">
          <span className="plan-suggest__label">Nearby</span>
          {suggestions.map((a) => (
            <Button key={a.icao} size="sm" variant="secondary" icon="plus" className="num" onClick={() => add(a.icao)} title={`${a.name} · ${fmt.distance(distanceM(c, a))}`}>
              {a.icao}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
